if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.slides = [
          ...this.querySelectorAll('.vessune-lightbox__slide')
        ];

        this.thumbs = [
          ...this.querySelectorAll('.vessune-lightbox__thumb')
        ];

        this.counter =
          this.querySelector('[data-vessune-counter]');

        this.stage =
          this.querySelector('.vessune-lightbox__stage');

        this.activeIndex = 0;

        this.scale = 1;
        this.x = 0;
        this.y = 0;

        this.minScale = 1;
        this.maxScale = 4;

        this.startScale = 1;
        this.startDistance = 0;

        this.startX = 0;
        this.startY = 0;

        this.panOriginX = 0;
        this.panOriginY = 0;

        this.panStartX = 0;
        this.panStartY = 0;

        this.swipeStartX = 0;
        this.swipeStartY = 0;

        this.pointerDown = false;
        this.dragged = false;

        this.lastTap = 0;

        this.bindControls();
        this.bindTouch();
        this.bindDesktop();
        this.bindKeyboard();
      }

      bindControls() {
        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            this.setActive(
              Number(thumb.dataset.index)
            );
          });
        });

        this.querySelector('.vessune-lightbox__prev')
          ?.addEventListener('click', () => {
            this.setActive(
              this.activeIndex - 1
            );
          });

        this.querySelector('.vessune-lightbox__next')
          ?.addEventListener('click', () => {
            this.setActive(
              this.activeIndex + 1
            );
          });

        this.querySelector('.vessune-lightbox__layout')
          ?.addEventListener('pointerup', (event) => {
            event.stopPropagation();
          });
      }

      bindTouch() {
        if (!this.stage) return;

        this.stage.addEventListener(
          'touchstart',
          (event) => this.touchStart(event),
          { passive: false }
        );

        this.stage.addEventListener(
          'touchmove',
          (event) => this.touchMove(event),
          { passive: false }
        );

        this.stage.addEventListener(
          'touchend',
          (event) => this.touchEnd(event),
          { passive: false }
        );

        this.stage.addEventListener(
          'touchcancel',
          () => {
            this.dragged = false;
          },
          { passive: true }
        );
      }

      bindDesktop() {
        if (!this.stage) return;

        this.stage.addEventListener(
          'pointerdown',
          (event) => {
            if (
              event.pointerType === 'touch' ||
              this.scale <= 1
            ) {
              return;
            }

            const image = this.activeImage;

            if (!image) return;

            event.preventDefault();

            this.pointerDown = true;
            this.dragged = false;

            this.panStartX = event.clientX;
            this.panStartY = event.clientY;

            this.panOriginX = this.x;
            this.panOriginY = this.y;

            this.stage.setPointerCapture?.(
              event.pointerId
            );

            this.stage.classList.add(
              'is-dragging'
            );
          }
        );

        this.stage.addEventListener(
          'pointermove',
          (event) => {
            if (
              !this.pointerDown ||
              event.pointerType === 'touch'
            ) {
              return;
            }

            const dx =
              event.clientX - this.panStartX;

            const dy =
              event.clientY - this.panStartY;

            if (
              Math.abs(dx) > 3 ||
              Math.abs(dy) > 3
            ) {
              this.dragged = true;
            }

            this.x =
              this.panOriginX + dx;

            this.y =
              this.panOriginY + dy;

            this.clampPan();
            this.applyTransform();
          }
        );

        const pointerEnd = (event) => {
          if (!this.pointerDown) return;

          this.pointerDown = false;

          this.stage.classList.remove(
            'is-dragging'
          );

          this.stage.releasePointerCapture?.(
            event.pointerId
          );
        };

        this.stage.addEventListener(
          'pointerup',
          pointerEnd
        );

        this.stage.addEventListener(
          'pointercancel',
          pointerEnd
        );

        this.stage.addEventListener(
          'click',
          (event) => {
            if (
              event.pointerType === 'touch' ||
              this.dragged
            ) {
              this.dragged = false;
              return;
            }

            if (!this.activeImage) return;

            if (this.scale > 1) {
              this.resetZoom(true);
            } else {
              this.zoomDesktop();
            }
          }
        );
      }

      bindKeyboard() {
        this.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();

              if (this.scale > 1) {
                this.resetZoom(true);
              }

              this.setActive(
                this.activeIndex - 1
              );
            }

            if (event.key === 'ArrowRight') {
              event.preventDefault();

              if (this.scale > 1) {
                this.resetZoom(true);
              }

              this.setActive(
                this.activeIndex + 1
              );
            }

            if (
              event.key === 'Escape' &&
              this.scale > 1
            ) {
              event.stopPropagation();
              this.resetZoom(true);
            }
          }
        );
      }

      show(opener) {
        super.show(opener);

        const mediaId =
          opener?.getAttribute('data-media-id');

        const index = this.slides.findIndex(
          (slide) =>
            slide.dataset.vessuneMediaId ===
            mediaId
        );

        this.setActive(
          index >= 0 ? index : 0
        );
      }

      hide() {
        this.resetZoom(false);

        window.pauseAllMedia?.();

        super.hide();
      }

      get activeSlide() {
        return this.slides[this.activeIndex];
      }

      get activeImage() {
        return this.activeSlide?.querySelector(
          '.vessune-lightbox__image'
        );
      }

      distance(touches) {
        const dx =
          touches[0].clientX -
          touches[1].clientX;

        const dy =
          touches[0].clientY -
          touches[1].clientY;

        return Math.hypot(dx, dy);
      }

      touchStart(event) {
        if (!this.activeImage) return;

        if (event.touches.length === 2) {
          event.preventDefault();

          this.startDistance =
            this.distance(event.touches);

          this.startScale =
            this.scale;

          this.stage.classList.add(
            'is-pinching'
          );

          return;
        }

        if (event.touches.length !== 1) {
          return;
        }

        const touch =
          event.touches[0];

        this.swipeStartX =
          touch.clientX;

        this.swipeStartY =
          touch.clientY;

        this.dragged = false;

        if (this.scale > 1) {
          event.preventDefault();

          this.panStartX =
            touch.clientX;

          this.panStartY =
            touch.clientY;

          this.panOriginX =
            this.x;

          this.panOriginY =
            this.y;
        }
      }

      touchMove(event) {
        if (!this.activeImage) return;

        if (event.touches.length === 2) {
          event.preventDefault();

          const newDistance =
            this.distance(event.touches);

          if (!this.startDistance) return;

          this.scale =
            this.startScale *
            (
              newDistance /
              this.startDistance
            );

          this.scale = Math.min(
            this.maxScale,
            Math.max(
              this.minScale,
              this.scale
            )
          );

          if (this.scale <= 1.01) {
            this.x = 0;
            this.y = 0;
          }

          this.clampPan();
          this.applyTransform();

          return;
        }

        if (
          event.touches.length === 1 &&
          this.scale > 1
        ) {
          event.preventDefault();

          const touch =
            event.touches[0];

          const dx =
            touch.clientX -
            this.panStartX;

          const dy =
            touch.clientY -
            this.panStartY;

          if (
            Math.abs(dx) > 3 ||
            Math.abs(dy) > 3
          ) {
            this.dragged = true;
          }

          this.x =
            this.panOriginX + dx;

          this.y =
            this.panOriginY + dy;

          this.clampPan();
          this.applyTransform();
        }
      }

      touchEnd(event) {
        this.stage?.classList.remove(
          'is-pinching'
        );

        if (event.touches.length > 0) {
          return;
        }

        if (this.scale < 1.04) {
          this.resetZoom(true);
        }

        if (this.scale > 1) {
          return;
        }

        const touch =
          event.changedTouches[0];

        if (!touch) return;

        const dx =
          touch.clientX -
          this.swipeStartX;

        const dy =
          touch.clientY -
          this.swipeStartY;

        if (
          Math.abs(dx) > 55 &&
          Math.abs(dx) >
            Math.abs(dy) * 1.25
        ) {
          this.setActive(
            this.activeIndex +
            (dx < 0 ? 1 : -1)
          );

          return;
        }

        const now = Date.now();

        if (
          now - this.lastTap < 300
        ) {
          if (this.scale > 1) {
            this.resetZoom(true);
          } else {
            this.scale = 2.2;
            this.applyTransform(true);
          }

          this.lastTap = 0;
        } else {
          this.lastTap = now;
        }
      }

      zoomDesktop() {
        this.scale = 2.2;
        this.x = 0;
        this.y = 0;

        this.applyTransform(true);
      }

      clampPan() {
        const image =
          this.activeImage;

        if (!image || this.scale <= 1) {
          this.x = 0;
          this.y = 0;
          return;
        }

        const imageRect =
          image.getBoundingClientRect();

        const stageRect =
          this.stage.getBoundingClientRect();

        const baseWidth =
          imageRect.width /
          this.scale;

        const baseHeight =
          imageRect.height /
          this.scale;

        const scaledWidth =
          baseWidth *
          this.scale;

        const scaledHeight =
          baseHeight *
          this.scale;

        const maxX = Math.max(
          0,
          (scaledWidth -
            stageRect.width) /
            2
        );

        const maxY = Math.max(
          0,
          (scaledHeight -
            stageRect.height) /
            2
        );

        this.x = Math.min(
          maxX,
          Math.max(-maxX, this.x)
        );

        this.y = Math.min(
          maxY,
          Math.max(-maxY, this.y)
        );
      }

      applyTransform(animate = false) {
        const image =
          this.activeImage;

        if (!image) return;

        if (this.scale > 1.01) {
          this.classList.add(
            'is-zoomed'
          );

          this.stage.classList.add(
            'is-zoomed'
          );
        } else {
          this.classList.remove(
            'is-zoomed'
          );

          this.stage.classList.remove(
            'is-zoomed'
          );
        }

        image.style.transition =
          animate
            ? 'transform .25s ease'
            : 'none';

        image.style.transform =
          `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      }

      resetZoom(animate = false) {
        const image =
          this.activeImage;

        this.scale = 1;
        this.x = 0;
        this.y = 0;

        this.classList.remove(
          'is-zoomed'
        );

        this.stage?.classList.remove(
          'is-zoomed',
          'is-pinching',
          'is-dragging'
        );

        if (!image) return;

        image.style.transition =
          animate
            ? 'transform .25s ease'
            : 'none';

        image.style.transform =
          'translate3d(0,0,0) scale(1)';
      }

      setActive(index) {
        if (!this.slides.length) return;

        this.resetZoom(false);

        index =
          (
            index +
            this.slides.length
          ) %
          this.slides.length;

        this.activeIndex = index;

        this.slides.forEach(
          (slide, slideIndex) => {
            const active =
              slideIndex === index;

            slide.hidden =
              !active;

            slide.classList.toggle(
              'active',
              active
            );
          }
        );

        this.thumbs.forEach(
          (thumb, thumbIndex) => {
            const active =
              thumbIndex === index;

            thumb.classList.toggle(
              'is-active',
              active
            );

            thumb.setAttribute(
              'aria-current',
              active
                ? 'true'
                : 'false'
            );
          }
        );

        if (this.counter) {
          this.counter.textContent =
            `${index + 1} / ${this.slides.length}`;
        }

        this.thumbs[index]
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });

        const deferred =
          this.activeSlide?.querySelector(
            'deferred-media'
          );

        if (
          deferred?.querySelector(
            'template'
          )
        ) {
          deferred.loadContent();
        }
      }
    }
  );
}